import React, { useEffect, useState, useCallback } from 'react'

import { AlertCircle, Check, Gem, Info, Minus, Plus } from 'lucide-react'

import { Zone } from '@/core/api/entities/zone/zone'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Badge } from '@/shared/components/atoms/Badge/Badge'
import { Button } from '@/shared/components/atoms/Button'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

import { RemoveZonesModal } from './RemoveZonesModal'

type ModalStep = 'form' | 'select_zones' | 'confirm_removal' | 'success'

interface ManageZonesModalProps {
	visible: boolean
	onClose: () => void
	onConfirm: (newZonesCount: number, zonesToRemove?: number[]) => Promise<void>
	planName: string
	currentZonesCount: number
	activeZonesCount: number // The number of zones currently active (can't go below this)
	pricePerZone: number
	currency: string
	billingCycle: string
	minZones?: number
	maxZones?: number
	/** List of zones for selection when downgrading */
	zones?: Zone[]
	/** Whether zones are loading */
	zonesLoading?: boolean
	/** Whether the account is in trial mode */
	isInTrial?: boolean
	/** Callback when user clicks upgrade (trial users with > 1 zone) */
	onUpgrade?: () => void
}

// Success View Component
const SuccessView: React.FC<{
	onClose: () => void
}> = ({ onClose }) => {
	const { t } = useTranslation('common')

	return (
		<div className="px-6 py-12 flex flex-col items-center justify-center text-center">
			{/* Success Icon */}
			<div className="w-24 h-24 rounded-2xl bg-content-tertiary/20 flex items-center justify-center mb-8">
				<Check className="h-12 w-12 text-foreground" />
			</div>

			{/* Title */}
			<h2 className="text-2xl font-semibold mb-4">
				{t('account.manage_zones_modal.success_title')}
			</h2>

			{/* Description */}
			<p className="text-content-tertiary mb-8 max-w-md">
				{t('account.manage_zones_modal.success_description')}
			</p>

			{/* Button */}
			<Button variant="outline" onClick={onClose} className="rounded-full px-8">
				{t('account.manage_zones_modal.back_to_mybrandtrack')}
			</Button>
		</div>
	)
}

// Confirm Removal View Component
const ConfirmRemovalView: React.FC<{
	onCancel: () => void
	onConfirm: () => void
	isConfirming: boolean
	zonesCount: number
}> = ({ onCancel, onConfirm, isConfirming, zonesCount }) => {
	const { t } = useTranslation('common')

	return (
		<div className="px-6 py-8">
			{/* Title */}
			<DialogTitle className="text-xl font-semibold mb-3">
				{t('account.confirm_removal_modal.title', {
					defaultValue: 'Remove zone',
				})}
			</DialogTitle>

			{/* Description */}
			<DialogDescription className="text-content-tertiary mb-8">
				{zonesCount === 1
					? t('account.confirm_removal_modal.description_single', {
							defaultValue:
								'This action will permanently remove the selected zone and stop music playback in that location.',
						})
					: t('account.confirm_removal_modal.description_multiple', {
							count: zonesCount,
							defaultValue: `This action will permanently remove the ${zonesCount} selected zones and stop music playback in those locations.`,
						})}
			</DialogDescription>

			{/* Buttons */}
			<div className="flex justify-end gap-3">
				<Button
					type="button"
					variant="ghost"
					onClick={onCancel}
					disabled={isConfirming}
					className="rounded-full px-6"
				>
					{t('account.confirm_removal_modal.cancel', {
						defaultValue: 'Cancel',
					})}
				</Button>
				<Button
					type="button"
					variant="destructive"
					onClick={onConfirm}
					disabled={isConfirming}
					className="rounded-full px-6"
				>
					{isConfirming
						? t('account.confirm_removal_modal.confirming', {
								defaultValue: 'Removing...',
							})
						: t('account.confirm_removal_modal.confirm', {
								defaultValue: 'Confirm removal',
							})}
				</Button>
			</div>
		</div>
	)
}

export const ManageZonesModal: React.FC<ManageZonesModalProps> = ({
	visible,
	onClose,
	onConfirm,
	planName,
	currentZonesCount,
	activeZonesCount,
	pricePerZone,
	currency,
	billingCycle,
	minZones = 1,
	maxZones = 100,
	zones = [],
	zonesLoading = false,
	isInTrial = false,
	onUpgrade,
}) => {
	const { t, currentLanguage } = useTranslation('common')
	const [step, setStep] = useState<ModalStep>('form')

	// Format price with locale-aware number formatting
	const formatPrice = useCallback(
		(amount: number): string => {
			return new Intl.NumberFormat(currentLanguage || 'en', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			}).format(amount)
		},
		[currentLanguage]
	)
	const [zonesCount, setZonesCount] = useState(currentZonesCount)
	const [isConfirming, setIsConfirming] = useState(false)
	// Store selected zones to remove for confirmation step
	const [selectedZonesToRemove, setSelectedZonesToRemove] = useState<number[]>(
		[]
	)

	// Reset state when modal opens
	useEffect(() => {
		if (visible) {
			setZonesCount(currentZonesCount)
			setStep('form')
			setIsConfirming(false)
			setSelectedZonesToRemove([])
		}
	}, [visible, currentZonesCount])

	const total = pricePerZone * zonesCount
	const hasChanges = zonesCount !== currentZonesCount
	const isDowngrading = zonesCount < currentZonesCount
	// Calculate how many zones need to be removed
	const zonesToRemoveCount = isDowngrading ? activeZonesCount - zonesCount : 0
	// Only show warning when user is trying to reduce below the number of active zones
	const isBelowActiveZones =
		activeZonesCount > 0 && zonesCount < activeZonesCount

	const handleDecrement = () => {
		if (zonesCount > minZones) {
			setZonesCount((prev) => prev - 1)
		}
	}

	const handleIncrement = () => {
		if (zonesCount < maxZones) {
			setZonesCount((prev) => prev + 1)
		}
	}

	const handleClose = () => {
		setZonesCount(currentZonesCount)
		setStep('form')
		setIsConfirming(false)
		setSelectedZonesToRemove([])
		onClose()
	}

	const handleConfirm = async () => {
		if (!hasChanges) return

		// If downgrading and would go below active zones, show zone selection
		if (isBelowActiveZones && zones.length > 0) {
			setStep('select_zones')
			return
		}

		// Otherwise proceed with confirmation
		setIsConfirming(true)
		try {
			await onConfirm(zonesCount)
			setStep('success')
		} catch (error) {
			console.error('Failed to update zones:', error)
		} finally {
			setIsConfirming(false)
		}
	}

	// Called when zones are selected in RemoveZonesModal - go to confirmation
	const handleZonesSelected = async (selectedZoneIds: number[]) => {
		setSelectedZonesToRemove(selectedZoneIds)
		setStep('confirm_removal')
	}

	// Called when user confirms the removal in the confirmation modal
	const handleConfirmRemoval = async () => {
		setIsConfirming(true)
		try {
			await onConfirm(zonesCount, selectedZonesToRemove)
			setStep('success')
		} catch (error) {
			console.error('Failed to remove zones:', error)
			// Go back to form on error
			setStep('form')
		} finally {
			setIsConfirming(false)
		}
	}

	const handleBackFromZoneSelection = () => {
		setStep('form')
	}

	const handleBackFromConfirmation = () => {
		setStep('select_zones')
	}

	// Show RemoveZonesModal when in select_zones step
	if (step === 'select_zones') {
		return (
			<RemoveZonesModal
				visible={true}
				onClose={handleBackFromZoneSelection}
				onConfirm={handleZonesSelected}
				zones={zones}
				requiredRemovalCount={zonesToRemoveCount}
				isLoading={zonesLoading}
			/>
		)
	}

	return (
		<Dialog open={visible} onOpenChange={handleClose}>
			<DialogContent
				className="sm:max-w-[600px] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col"
				showCloseButton={false}
			>
				{step === 'success' ? (
					<SuccessView onClose={handleClose} />
				) : step === 'confirm_removal' ? (
					<ConfirmRemovalView
						onCancel={handleBackFromConfirmation}
						onConfirm={handleConfirmRemoval}
						isConfirming={isConfirming}
						zonesCount={selectedZonesToRemove.length}
					/>
				) : (
					<>
						{/* Header */}
						<div className="px-6 py-5 border-b">
							<DialogTitle className="text-xl font-semibold">
								{t('account.manage_zones_modal.title')}
							</DialogTitle>
							<DialogDescription className="text-content-tertiary mt-1">
								{t('account.manage_zones_modal.subtitle')}
							</DialogDescription>
						</div>

						{/* Content */}
						<div className="px-6 py-6 space-y-4 overflow-y-auto flex-1">
							{/* Summary Card */}
							<div className="rounded-lg border border-border bg-card-dark">
								{/* Plan header with zone counter */}
								<div className="p-4 border-b border-dashed border-border">
									<div className="flex items-center justify-between mb-4">
										<h4 className="font-semibold text-lg">{planName}</h4>
										<Badge
											variant="secondary"
											className="text-sm bg-content-tertiary/20 text-content-secondary rounded-sm"
										>
											{currentZonesCount}{' '}
											{t('account.manage_zones_modal.zones')}
										</Badge>
									</div>

									{/* Zone counter */}
									<div className="flex items-center justify-between">
										<span className="text-content-secondary">
											{t('account.manage_zones_modal.number_of_zones')}
										</span>
										<div className="flex items-center gap-3">
											<button
												onClick={handleDecrement}
												disabled={zonesCount <= minZones || isConfirming}
												className="h-10 w-10 rounded-full bg-content-tertiary/20 flex items-center justify-center hover:bg-content-tertiary/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
											>
												<Minus className="h-5 w-5" />
											</button>
											<span className="text-2xl font-semibold w-8 text-center">
												{zonesCount}
											</span>
											<button
												onClick={handleIncrement}
												disabled={zonesCount >= maxZones || isConfirming}
												className="h-10 w-10 rounded-full bg-content-tertiary/20 flex items-center justify-center hover:bg-content-tertiary/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
											>
												<Plus className="h-5 w-5" />
											</button>
										</div>
									</div>

									{/* Info when trying to reduce below active zones */}
									{isBelowActiveZones && (
										<div className="flex items-start gap-2 mt-3 text-status-warning">
											<AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
											<p className="text-sm">
												{t('account.manage_zones_modal.select_zones_info', {
													count: zonesToRemoveCount,
													defaultValue: `You'll need to select ${zonesToRemoveCount} zone(s) to remove.`,
												})}
											</p>
										</div>
									)}
								</div>

								{/* Price details */}
								<div className="p-4">
									<div className="flex items-start justify-between">
										<div>
											<p className="text-content-secondary">{planName}</p>
											<p className="text-sm text-content-tertiary">
												x{zonesCount} {t('account.manage_zones_modal.zone')}
											</p>
										</div>
										<div className="text-right">
											<span className="text-lg font-semibold">
												{formatPrice(pricePerZone)} {currency}
											</span>
											<span className="text-sm text-content-tertiary">
												/{t('account.manage_zones_modal.zone')}
											</span>
										</div>
									</div>
								</div>

								<Separator />

								{/* Total section */}
								<div className="p-4">
									<div className="flex items-start justify-between">
										<div>
											<p className="text-2xl font-semibold">
												{t('account.manage_zones_modal.total')}
											</p>
											<p className="text-sm text-content-tertiary">
												{t('account.manage_zones_modal.billed_cycle', {
													cycle: billingCycle,
												})}
											</p>
										</div>
										<div className="text-right">
											<p className="text-2xl font-semibold">
												{formatPrice(total)} {currency}
											</p>
											<p className="text-sm text-content-tertiary">
												{t('account.manage_zones_modal.excluding_tax')}
											</p>
										</div>
									</div>
								</div>
							</div>

							{/* Info notice */}
							<div className="flex items-start gap-3 p-4 rounded-lg border border-dashed border-border">
								<Info className="h-5 w-5 text-content-tertiary flex-shrink-0 mt-0.5" />
								<p className="text-sm text-content-secondary">
									{t('account.manage_zones_modal.proration_notice')}
								</p>
							</div>
						</div>

						{/* Footer */}
						<div className="px-6 py-5 border-t flex flex-col lg:flex-row justify-end gap-3">
							<Button
								type="button"
								variant="ghost"
								onClick={handleClose}
								disabled={isConfirming}
								className="rounded-full px-6"
							>
								{t('account.manage_zones_modal.cancel')}
							</Button>
							<Button
								type="button"
								variant="default"
								onClick={
									isInTrial && zonesCount > 1 && onUpgrade
										? onUpgrade
										: handleConfirm
								}
								disabled={!hasChanges || isConfirming}
								className="rounded-full px-6"
							>
								{isInTrial && zonesCount > 1 && (
									<Gem className="w-4 h-4 mr-2" />
								)}
								{isConfirming
									? t('account.manage_zones_modal.confirming')
									: isBelowActiveZones
										? t('account.manage_zones_modal.select_zones', {
												defaultValue: 'Select zones',
											})
										: t('account.manage_zones_modal.confirm')}
							</Button>
						</div>
					</>
				)}
			</DialogContent>
		</Dialog>
	)
}

export default ManageZonesModal
